import React, { useState } from 'react';
import { createNewClinicAdmin } from '../services/api.ts';

const NewClinicAdminPage = () => {
  const [clientEmail, setClientEmail] = useState('');
  const [clinicName, setClinicName] = useState('');
  const [clinicCnpj, setClinicCnpj] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Função para formatar CNPJ
  const formatCNPJ = (value: string) => {
    const cnpj = value.replace(/\D/g, '');
    return cnpj
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2')
      .substring(0, 18);
  };

  // Funções de validação
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateCNPJ = (cnpj: string) => {
    const numbers = cnpj.replace(/\D/g, '');
    return numbers.length === 14;
  };

  // Handlers
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setClientEmail(value);
    if (validateEmail(value)) {
      setError('');
    }
  };

  const handleCNPJChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCNPJ(e.target.value);
    setClinicCnpj(formatted);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validações
    if (!validateEmail(clientEmail)) {
      setError('Por favor, insira um email válido');
      return;
    }

    if (!validateCNPJ(clinicCnpj)) {
      setError('Por favor, insira um CNPJ válido');
      return;
    }

    setIsLoading(true);

    try {
      const cleanCNPJ = clinicCnpj.replace(/\D/g, '');
      const data = await createNewClinicAdmin(clientEmail, clinicName, cleanCNPJ);
      setSuccess(data.message || 'Nova clínica criada com sucesso!');
      
      // Limpa o formulário
      setClientEmail('');
      setClinicName('');
      setClinicCnpj('');
    } catch (err: any) {
      setError(err.message || 'Erro ao criar nova clínica!');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-8 bg-gray-50">
      <div className="w-full max-w-md">
        {/* Card do Formulário */}
        <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-200">
          {/* Cabeçalho do Card */}
          <div className="space-y-2 mb-6">
            <h2 className="text-2xl font-semibold tracking-tight">
              Criar Nova Clínica
            </h2>
            <p className="text-sm text-gray-500">
              Preencha os dados para criar uma nova clínica no sistema
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 rounded-md bg-destructive/15 text-destructive text-sm border border-destructive/20">
              {error}
            </div>
          )}
          
          {success && (
            <div className="mb-6 p-4 rounded-md bg-green-50 text-green-600 text-sm border border-green-200">
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                E-mail do Cliente
              </label>
              <input
                type="email"
                required
                value={clientEmail}
                onChange={handleEmailChange}
                onBlur={() => {
                  if (clientEmail && !validateEmail(clientEmail)) {
                    setError('Por favor, insira um email válido');
                  }
                }}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm
                  placeholder:text-muted-foreground
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
                  disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="exemplo@dominio.com"
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Nome/Razão Social da Clínica
              </label>
              <input
                type="text"
                required
                value={clinicName}
                onChange={(e) => setClinicName(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm
                  placeholder:text-muted-foreground
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
                  disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Ex: Clínica Sorriso Saudável"
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                CNPJ
              </label>
              <input
                type="text"
                required
                value={clinicCnpj}
                onChange={handleCNPJChange}
                onBlur={() => {
                  if (clinicCnpj && !validateCNPJ(clinicCnpj)) {
                    setError('Por favor, insira um CNPJ válido');
                  }
                }}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm
                  placeholder:text-muted-foreground
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
                  disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="12.345.678/0001-09"
                maxLength={18}
                disabled={isLoading}
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full h-10 px-4 py-2 rounded-md text-sm font-medium text-primary-foreground
                ${isLoading 
                  ? 'bg-primary/80 cursor-not-allowed' 
                  : 'bg-primary hover:bg-primary/90'
                }
                inline-flex items-center justify-center whitespace-nowrap
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
                transition-colors`}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Criando...
                </>
              ) : (
                'Criar Clínica'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NewClinicAdminPage;